/** The Tags column: tags per guest as `min–max` ("0–1", "1–∞"). In edit mode a button opens
 *  a popover with Min / Max (0 = optional / unlimited) and four presets. Only what the
 *  operator edits changes: a stored 32767 ("many", as the dashboard writes it) stays 32767
 *  unless Max is typed, and the "many" presets keep it. */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, ButtonBase, Popover, TextField, Typography } from '@mui/material';
import { useId, useState } from 'react';

import { visuallyHidden } from '@/components/BoolText';
import type { TriggerProps } from '@/components/DataGrid/CellActionsPopover';

import {
  applyPreset,
  COUNT_PRESETS,
  describeCounts,
  formatCounts,
  isUnlimited,
  MAX_TAGS_UNLIMITED,
  presetOf,
} from './flags';

/** The counts as text (view mode, read-only rows). */
export function CountsText({ min, max, muted }: { min: number; max: number; muted?: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 13,
        fontVariantNumeric: 'tabular-nums',
        color: muted ? 'text.secondary' : 'text.primary',
      }}
    >
      {formatCounts(min, max)}
      <Box component="span" sx={visuallyHidden}>
        {`, ${describeCounts(min, max)}`}
      </Box>
    </Box>
  );
}

export function TagsCountButton({
  min,
  max,
  name,
  onSet,
  navProps,
}: {
  min: number;
  max: number;
  /** The row's name ("MEAL") — the button is "Tags per guest of MEAL". */
  name: string;
  onSet: (min: number, max: number) => void;
  navProps: TriggerProps;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const describedBy = useId();
  const label = `Tags per guest of ${name}`;
  return (
    <>
      <ButtonBase
        aria-label={label}
        aria-describedby={describedBy}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        onClick={(e) => setAnchor(e.currentTarget)}
        {...navProps}
        sx={{
          height: 26,
          pl: 0.75,
          pr: 0.25,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          fontSize: 13,
          fontVariantNumeric: 'tabular-nums',
          '&:hover': { borderColor: 'text.secondary' },
          '&.Mui-focusVisible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 1,
          },
        }}
      >
        {formatCounts(min, max)}
        <ArrowDropDownIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />
      </ButtonBase>
      <Box component="span" id={describedBy} sx={visuallyHidden}>
        {describeCounts(min, max)}
      </Box>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { role: 'dialog', 'aria-label': label, sx: { p: 1.5, width: 300 } } }}
      >
        <CountsEditor
          min={min}
          max={max}
          name={name}
          onSet={onSet}
          onDone={() => setAnchor(null)}
        />
      </Popover>
    </>
  );
}

const WHOLE = /^\d+$/;

const isCount = (s: string) => WHOLE.test(s.trim()) && Number(s) <= MAX_TAGS_UNLIMITED;

/** What is wrong with typed counts, and which field it concerns (null: they apply). */
export function countsProblem(
  min: string,
  max: string,
): { field: 'min' | 'max' | 'both'; message: string } | null {
  if (!isCount(min)) return { field: 'min', message: 'Min must be a whole number from 0' };
  if (!isCount(max)) return { field: 'max', message: 'Max must be a whole number from 0' };
  if (!isUnlimited(Number(max)) && Number(min) > Number(max))
    return { field: 'both', message: 'Min is above Max' };
  return null;
}

function CountsEditor({
  min,
  max,
  name,
  onSet,
  onDone,
}: {
  min: number;
  max: number;
  name: string;
  onSet: (min: number, max: number) => void;
  onDone: () => void;
}) {
  const [minText, setMinText] = useState(String(min));
  const [maxText, setMaxText] = useState(String(max));
  const problem = countsProblem(minText, maxText);
  const current = presetOf(min, max);
  const commit = (mi: string, ma: string) => {
    if (!countsProblem(mi, ma)) onSet(Number(mi), Number(ma));
  };
  const fieldSx = { '& .MuiInputBase-input': { fontVariantNumeric: 'tabular-nums' } };
  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <Typography variant="subtitle2" component="h3" sx={{ lineHeight: 1.4 }}>
        Tags per guest · {name}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <TextField
          label="Min"
          size="small"
          value={minText}
          autoFocus
          error={problem !== null && problem.field !== 'max'}
          helperText="0 = optional"
          onChange={(e) => {
            setMinText(e.target.value);
            commit(e.target.value, maxText);
          }}
          inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
          sx={fieldSx}
        />
        <TextField
          label="Max"
          size="small"
          value={maxText}
          error={problem !== null && problem.field !== 'min'}
          helperText={
            isCount(maxText) && isUnlimited(Number(maxText)) ? 'Unlimited' : '0 = unlimited'
          }
          onChange={(e) => {
            setMaxText(e.target.value);
            commit(minText, e.target.value);
          }}
          inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
          sx={fieldSx}
        />
      </Box>
      {/* Reserved: a problem appearing moves nothing. */}
      <Typography
        variant="caption"
        color="error"
        role={problem ? 'alert' : undefined}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 20 }}
      >
        {problem && (
          <>
            <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
            {problem.message} — not applied
          </>
        )}
      </Typography>
      <Box
        role="group"
        aria-label="Presets"
        sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}
      >
        {COUNT_PRESETS.map((p) => {
          const next = applyPreset(p, { max_tags: max });
          const on = current?.id === p.id;
          return (
            <Button
              key={p.id}
              size="small"
              variant={on ? 'contained' : 'outlined'}
              aria-pressed={on}
              onClick={() => {
                onSet(next.min_tags, next.max_tags);
                onDone();
              }}
              sx={{ justifyContent: 'space-between', px: 1, fontWeight: 600 }}
            >
              {p.label}
              <Box
                component="span"
                sx={{
                  fontWeight: 400,
                  fontVariantNumeric: 'tabular-nums',
                  ml: 1,
                  color: on ? 'rgba(255,255,255,0.85)' : 'text.secondary',
                }}
              >
                {formatCounts(next.min_tags, next.max_tags)}
              </Box>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}
